import React, { startTransition, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SignupPage from "./SignupPage/SignupPage";
import VerifyOtpPage from "./VerifyOtpPage/VerifyOtpPage";
import CreatePasswordPage from "./CreatePasswordPage/CreatePasswordPage";
import GetVerifiedPage from "./GetVerifiedPage/GetVerifiedPage";
import RegisterSuccessPage from "./RegisterSuccessPage/RegisterSuccessPage";
import Modal from "../../components/common/modal/modal";
import Header from "../../components/common/Header/Header";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updateLoggedInUserDataInLocalStorage } from "../../config/config";
import performLogout from "../../utils/logoutHandler";
import AddressDetailsPage from "./addressDetails/addressDetailsPage";
import backArrow from "../../assets/images/greenbackarrow.svg";
import useScreenResolution from "../../hooks/useScreenResolution";
import useLanguageTranslate from "../../hooks/useLanguageTranslate";

// Enumeration for page states
const PageState = {
  SIGNUP: "SIGNUP",
  OTP_VERIFIED: "OTP_VERIFIED",
  PASSWORD_CREATED: "PASSWORD_CREATED",
  DOCS_VERIFIED: "DOCS_VERIFIED",
  ADDRESS_DETAILS: "ADDRESS_DETAILS",
  SUCCESS: "SUCCESS",
};

const MainSignupPage = () => {
  updateLoggedInUserDataInLocalStorage({ isLoggedIn: false });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [CPRExists, setCPRExists] = useState(false);
  const [currentPage, setCurrentPage] = useState(PageState.SIGNUP);
  const [showModal, setShowModal] = useState(true);
  const { mobile } = useScreenResolution();
  const translate = useLanguageTranslate();
  // Which way the next step enters (index.css, [data-auth-step]): set just
  // before the step changes, read when it renders.
  const navDirection = useRef("forward");

  // Handler for advancing to the next page
  const handleSuccess = (nextPage, isCPRExists = CPRExists) => {
    navDirection.current = "forward";
    setCPRExists(isCPRExists);
    setCurrentPage(nextPage);
  };

  // Handler for going back to the previous page
  const handleBackClicked = () => {
    navDirection.current = "back";
    startTransition(() => {
      switch (currentPage) {
        case PageState.OTP_VERIFIED:
          setCurrentPage(PageState.SIGNUP);
          break;
        case PageState.PASSWORD_CREATED:
          setCurrentPage(CPRExists ? PageState.SIGNUP : PageState.OTP_VERIFIED);
          break;
        case PageState.DOCS_VERIFIED:
          setCurrentPage(PageState.PASSWORD_CREATED);
          break;
        case PageState.ADDRESS_DETAILS:
          setCurrentPage(
            CPRExists ? PageState.PASSWORD_CREATED : PageState.DOCS_VERIFIED
          );
          break;
        case PageState.SUCCESS:
          setCurrentPage(PageState.ADDRESS_DETAILS);
          break;
        default:
          break;
      }
    });
  };

  // Component to render based on current page state
  const PageRenderer = () => {
    switch (currentPage) {
      case PageState.SIGNUP:
        return (
          <SignupPage
            pinActionOnMobile
            onSuccess={(isCPRExists) =>
              handleSuccess(
                isCPRExists
                  ? PageState.PASSWORD_CREATED
                  : PageState.OTP_VERIFIED,
                isCPRExists
              )
            }
          />
        );
      case PageState.OTP_VERIFIED:
        return (
          <VerifyOtpPage
            pinActionOnMobile
            pageTitle={translate("Verify OTP", "التحقق من الرمز")}
            onBackClick={handleBackClicked}
            onSuccess={() => handleSuccess(PageState.PASSWORD_CREATED)}
          />
        );
      case PageState.PASSWORD_CREATED:
        return (
          <CreatePasswordPage
            pinActionOnMobile
            onBackClick={handleBackClicked}
            onSuccess={() =>
              CPRExists
                ? handleSuccess(PageState.ADDRESS_DETAILS)
                : handleSuccess(PageState.DOCS_VERIFIED)
            }
          />
        );
      case PageState.DOCS_VERIFIED:
        return (
          <GetVerifiedPage
            pinActionOnMobile
            onBackClick={handleBackClicked}
            onSuccess={() => handleSuccess(PageState.ADDRESS_DETAILS)}
          />
        );
      case PageState.ADDRESS_DETAILS:
        return (
          <AddressDetailsPage
            pinActionOnMobile
            onSuccess={() => handleSuccess(PageState.SUCCESS)}
          />
        );
      case PageState.SUCCESS:
        return <RegisterSuccessPage />;
      default:
        return null;
    }
  };

  return (
    <Modal show={showModal}>
      <Header
        isPopup
        onCloseModal={() => {
          // The address step is a verification gate — abandoning it must clear
          // any partially established session rather than leave it behind.
          if (currentPage === PageState.ADDRESS_DETAILS) {
            performLogout({ dispatch, navigate });
            return;
          }
          startTransition(() => {
            setShowModal(false);
            navigate("/");
          });
        }}
        showBackBtn={
          currentPage != PageState.SIGNUP && currentPage != PageState.SUCCESS
        }
        backClickHandler={handleBackClicked}
      />
      {/* The header's own back button is desktop-only, so on a phone the
          OTP, password and verification steps had no way back (verification
          had the sign-up arrow, which went home). Pinned in the header row,
          opposite Close, stepping back through the flow; portalled so the
          page fade cannot hold it under the header. The first step keeps its
          own arrow home, the address step deliberately has none (Close logs
          out), and the success step is an end. */}
      {mobile &&
        [
          PageState.OTP_VERIFIED,
          PageState.PASSWORD_CREATED,
          PageState.DOCS_VERIFIED,
        ].includes(currentPage) &&
        createPortal(
          <button
            type="button"
            aria-label={translate("Back", "رجوع")}
            className="fixed top-[19px] left-4 rtl:left-auto rtl:right-4 z-30 flex w-10 h-10 items-center justify-center rounded-full transition-transform duration-150 ease-out active:scale-[0.94]"
            onClick={handleBackClicked}
          >
            <img src={backArrow} alt="" className="w-10 h-10 rtl:rotate-180" />
          </button>,
          document.body
        )}
      <div className="mainSignupModal">
        {/* Keyed by step so each one mounts fresh and plays its entrance. */}
        <div
          key={currentPage}
          data-auth-step
          data-auth-nav={navDirection.current}
        >
          {PageRenderer()}
        </div>
      </div>
    </Modal>
  );
};

export default MainSignupPage;
